import React, { useState } from 'react';
import { Menu, Settings, BookOpen, GraduationCap, User, LogOut, LayoutDashboard, HelpCircle, Coffee } from 'lucide-react';
import { Link } from 'react-router-dom';
import { type UserProfile } from '../types';
import { Guide } from './Guide';
import { DonationOptions } from './DonationOptions';
import { useTranslation } from '../utils/translations';
import { StreakBadge, StreakNotification } from './StreakNotification';

interface NavigationProps {
  user: UserProfile;
  showGuide: boolean;
  setShowGuide: (show: boolean) => void;
  onLanguageSelect: () => void;
  onLevelSelect: () => void;
  onSignOut: () => void;
  onViewAccount: () => void;
  onViewSettings: () => void;
}

export function Navigation({
  user,
  showGuide,
  setShowGuide,
  onLanguageSelect,
  onLevelSelect,
  onSignOut,
  onViewAccount,
  onViewSettings
}: NavigationProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDonation, setShowDonation] = useState(false);
  const [showStreakModal, setShowStreakModal] = useState(false);
  const { t, isRTL } = useTranslation(user.nativeLanguage);

  return (
    <>
      <nav className="bg-white shadow-lg" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center gap-4">
            <a href="https://listendutch.com" className="flex items-center hover:opacity-80 transition-opacity">
              <BookOpen className="h-8 w-8 text-indigo-600" />
              <span className="ml-2 text-xl font-semibold">Listen Dutch</span>
            </a>
            <StreakBadge
              userId={user.uid}
              nativeLanguage={user.nativeLanguage}
              onClick={() => setShowStreakModal(true)}
            />
          </div>

          <div className="flex items-center">
            <div className="hidden md:block">
              <div className="flex items-center space-x-4">
                <button
                  onClick={onLevelSelect}
                  className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <GraduationCap className="h-4 w-4 mr-2" />
                  {t('Current Level')}: {user.currentLevel}
                </button>
                <button
                  onClick={onViewSettings}
                  className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  {t('Settings')}
                </button>
                
                <button
                  onClick={() => setShowDonation(true)}
                  className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <Coffee className="h-4 w-4 mr-2" />
                  Buy me a coffee
                </button>

                <button
                  onClick={() => setShowGuide(true)}
                  className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  {t('Quick Start Guide')}
                </button>
                {user.isAdmin && (
                  <Link
                    to="/admin"
                    className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                  >
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    {t('Admin')}
                  </Link>
                )}
                <button
                  onClick={onSignOut}
                  className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('Logout')}
                </button>
              </div>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-indigo-600 focus:outline-none"
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <button
              onClick={() => {
                onLevelSelect();
                setIsMenuOpen(false);
              }}
              className="w-full text-left text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-base font-medium flex items-center"
            >
              <GraduationCap className="h-4 w-4 mr-2" />
              Level: {user.currentLevel}
            </button>
            <button
              onClick={() => {
                setShowDonation(true);
                setIsMenuOpen(false);
              }}
              className="w-full text-left text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-base font-medium flex items-center"
            >
              <Coffee className="h-4 w-4 mr-2" />
              Buy me a coffee
            </button>
            <button
              onClick={() => {
                setShowGuide(true);
                setIsMenuOpen(false);
              }}
              className="w-full text-left text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-base font-medium flex items-center"
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Guide
            </button>
            <button
              onClick={() => {
                onViewSettings();
                setIsMenuOpen(false);
              }}
              className="w-full text-left text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-base font-medium flex items-center"
            >
              <Settings className="h-4 w-4 mr-2" />
              Account Settings
            </button>
            {user.isAdmin && (
              <Link
                to="/admin"
                className="w-full text-left text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-base font-medium flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Admin
              </Link>
            )}
            <button
              onClick={() => {
                onSignOut();
                setIsMenuOpen(false);
              }}
              className="w-full text-left text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-base font-medium flex items-center"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </button>
          </div>
        </div>
      )}
      </nav>
      
      {showGuide && <Guide onClose={() => setShowGuide(false)} userId={user.uid} language={user.nativeLanguage} />}
      {showDonation && <DonationOptions onClose={() => setShowDonation(false)} />}
      {showStreakModal && (
        <StreakNotification
          userId={user.uid}
          nativeLanguage={user.nativeLanguage}
          onClose={() => setShowStreakModal(false)}
        />
      )}
    </>
  );
}